import Calendar from '@fullcalendar/react';
import listPlugin from '@fullcalendar/list';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import timelinePlugin from '@fullcalendar/timeline';
import interactionPlugin from '@fullcalendar/interaction';
import { EventInput, EventContentArg } from '@fullcalendar/core';

import { Box, Card, Stack, Typography } from '@mui/material';
import { useBoolean } from 'src/hooks/use-boolean';

import { useState, useEffect, useCallback } from 'react';
import LoadingContainer from 'src/components/ui-kit/loading-container';
import usePagination from 'src/hooks/use-pagination';
import { DayOffType } from 'src/type/day-off-config';
import { fParams, ParamsType } from 'src/utils/format-params';
import axios, { endpoints } from 'src/utils/axios';
import { Pagination } from 'src/type/utils';
import { toastError } from 'src/utils/format-error';
import dayjs from 'dayjs';
import DayOffCreateDialog from './day-off-create-dialog';
import CalendarToolbar from './calendar/calendar-toolbar';
import useCalendar from './calendar/hooks/use-calendar';
import { StyledCalendar } from './calendar/styles';
import DayOffViewDialog from './day-off-view-dialog';
import ConfirmRemoveDialog from './confirm-remove-dialog';
import SettingWeekly from './setting-weekly';

export default function SettingDayOff() {
  const reload = useBoolean();
  const initialize = useBoolean(true);
  const remove = useBoolean(false);

  const [events, setEvents] = useState<EventInput[]>([]);
  const [disableDates, setDisableDates] = useState<string[]>([]);

  const [selectedData, setSelectedData] = useState<DayOffType | undefined>(undefined);

  const [dayOffConfigs, setDayOffConfigs] = useState<DayOffType[]>([]);

  const { currentPage, prevPage, meta, onResetPage } = usePagination<DayOffType>();

  const {
    calendarRef,
    //
    date,
    //
    onDatePrev,
    onDateNext,
    onDateToday,
    //
    openForm,
    onOpenForm,
    openView,
    // onOpenView,
    onCloseForm,
    onCloseView,
    //
    onClickEvent,
    selectedEventId,
    // onSelectRange,
  } = useCalendar();

  const handleGetDayOff = useCallback(async (params: ParamsType) => {
    try {
      initialize.onTrue();
      const { data } = await axios.get<Pagination<DayOffType>>(endpoints.dayOffs.get, {
        params: fParams(params),
      });
      setDayOffConfigs(data.results);
    } catch (error) {
      toastError(error);
    } finally {
      initialize.onFalse();
    }
  }, []);

  const handleReload = useCallback(() => {
    onResetPage();
    reload.onToggle();
  }, []);

  useEffect(() => {
    const pageDirection = currentPage > prevPage ? 'next' : 'prev';
    const params = {
      page: currentPage,
      limit: 999,
      ...(pageDirection === 'next' && { lastDocId: meta.lastDocId }),
      ...(pageDirection === 'prev' && { firstDocId: meta.firstDocId }),
    };
    handleGetDayOff(params);
  }, [reload.value]);

  useEffect(() => {
    if (dayOffConfigs.length) {
      const mappedEvents = dayOffConfigs.map((item) => ({
        id: `${item.id}`,
        date: dayjs(item.date).format('YYYY-MM-DD'),
        extendedProps: {
          startTime: dayjs(item.startTime, 'HH:mm:ss').format('HH:mm'),
          endTime: dayjs(item.endTime, 'HH:mm:ss').format('HH:mm'),
          note: item.note,
        },
      }));
      setEvents(mappedEvents);
      setDisableDates(mappedEvents.map((event) => event.date));
    } else {
      setEvents([]);
      setDisableDates([]);
    }
  }, [dayOffConfigs]);

  const renderEventContent = (arg: EventContentArg) => {
    const { startTime, endTime, note } = arg.event.extendedProps;

    return (
      <Stack
        sx={{
          bgcolor: 'primary.main',
          height: 60,
          borderRadius: '4px',
          p: 1,
          cursor: 'pointer',
        }}
      >
        <Typography variant="caption" noWrap>
          {`${startTime} - ${endTime}`}
        </Typography>
        <Typography variant="caption" noWrap>
          {note}
        </Typography>
      </Stack>
    );
  };

  const findData = (eventId: string | null) =>
    eventId ? dayOffConfigs.find(({ id }) => String(id) === eventId) : undefined;

  const handleUpdate = () => {
    if (!selectedEventId) {
      onCloseView();
      return;
    }
    onCloseView();
    const data = findData(selectedEventId);
    if (!data) return;
    setSelectedData(data);
    onOpenForm();
  };

  const handleDelete = (id: string) => {
    onCloseView();
    const data = findData(id);
    if (!data) return;
    setSelectedData(data);
    remove.onTrue();
  };

  return (
    <>
      {/* Weekly Day Off */}
      <SettingWeekly />

      {/* Specific Day Off */}
      <LoadingContainer loading={initialize.value} sx={{ minHeight: '30vh' }} fitContent>
        <Card sx={{ py: 5, px: 3, overflowX: 'auto' }}>
          <Typography variant="h6">เลือกวันปิดระบบ</Typography>

          <Box sx={{ minWidth: 800 }}>
            <StyledCalendar>
              <CalendarToolbar
                date={date}
                loading={false}
                onNextDate={onDateNext}
                onPrevDate={onDatePrev}
                onToday={onDateToday}
                onCreate={onOpenForm}
              />

              <Calendar
                weekends
                editable
                // selectable
                rerenderDelay={10}
                allDayMaintainDuration
                eventResizableFromStart
                ref={calendarRef}
                initialDate={date}
                initialView="dayGridMonth"
                dayMaxEventRows={3}
                eventDisplay="block"
                events={events}
                headerToolbar={false}
                height="auto"
                // select={onSelectRange}
                eventClick={onClickEvent}
                eventContent={renderEventContent}
                plugins={[
                  listPlugin,
                  dayGridPlugin,
                  timelinePlugin,
                  timeGridPlugin,
                  interactionPlugin,
                ]}
              />
            </StyledCalendar>
          </Box>
        </Card>

        <DayOffCreateDialog
          open={openForm}
          onClose={onCloseForm}
          handleReload={handleReload}
          data={selectedData}
          clearSelectedData={() => setSelectedData(undefined)}
          disabledDates={disableDates}
        />

        <DayOffViewDialog
          open={openView}
          onClose={onCloseView}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
          data={findData(selectedEventId)}
          clearSelectedData={() => setSelectedData(undefined)}
        />

        <ConfirmRemoveDialog
          open={remove.value}
          onClose={remove.onFalse}
          data={selectedData}
          handleReload={handleReload}
          clearSelectedData={() => setSelectedData(undefined)}
        />
      </LoadingContainer>
    </>
  );
}
